import { z } from 'zod';

export const FREE_READING_SCHEMA_VERSION = 'free-v4' as const;

export function readingWordCount(value: string): number {
  return value.trim().split(/\s+/u).filter(Boolean).length;
}

function wordBoundedString(field: string, minimum: number, maximum: number, characterLimit = 4000) {
  return z.string().min(1).max(characterLimit).superRefine((value, context) => {
    const words = readingWordCount(value);
    if (words < minimum || words > maximum) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: `${field} must contain ${minimum}–${maximum} words; received ${words}.`,
      });
    }
  });
}

/** Current strict Structured Outputs target for newly generated free readings. */
export const freeReadingSchema = z.object({
  schema_version: z.literal(FREE_READING_SCHEMA_VERSION),
  title: wordBoundedString('title', 5, 10, 400),
  personalized_reading: wordBoundedString('personalized_reading', 140, 250).superRefine((value, context) => {
    if (/\r|\n/u.test(value)) {
      context.addIssue({ code: z.ZodIssueCode.custom, message: 'personalized_reading must be one cohesive paragraph.' });
    }
  }),
  selected_focus: z.enum(['love_and_relationships','money_and_career','purpose_and_direction','healing_and_inner_peace','whole_path']),
}).strict();

/** Historical free-v3 content remains display compatible, never a generation target. */
export const historicalFreeV3ReadingSchema = z.object({
  schema_version: z.literal('free-v3'),
  title: z.string(),
  celestial_signature: z.string(),
  current_cycle: z.string(),
  strength: z.string(),
  growth_edge: z.string(),
  selected_focus: z.enum(['love_and_relationships','money_and_career','purpose_and_direction','healing_and_inner_peace','whole_path']),
}).strict();

/** Historical free-v2 content remains display compatible, never a generation target. */
export const historicalFreeV2ReadingSchema = z.object({
  schema_version: z.literal('free-v2'),
  title: z.string(),
  celestial_signature: z.string(),
  core_energy: z.string(),
  current_cycle: z.string(),
  strength: z.string(),
  growth_edge: z.string(),
  sales_bridge: z.string(),
  selected_focus_label: z.string(),
}).strict();

/** Historical free content remains display compatible, never a generation target. */
export const legacyFreeReadingSchema = z.object({
  title: z.string(),
  celestial_signature: z.string(),
  core_pattern: z.string(),
  focus_insight: z.string(),
  current_cycle: z.string(),
  strength: z.string(),
  growth_edge: z.string(),
  paid_teaser: z.string(),
}).strict();

const legacyNarrativeSectionSchema = z.object({
  overview: z.string(),
  strength: z.string(),
  growth_edge: z.string(),
}).strict();

const numerologySchema = z.object({
  life_path: z.string(),
  birthday_number: z.string(),
  personal_year: z.string(),
  overview: z.string(),
}).strict();

const celestialSignatureSchema = z.object({
  overview: z.string(),
  sun: z.string(),
  moon: z.string(),
  mercury: z.string(),
  venus: z.string(),
  mars: z.string(),
  jupiter: z.string(),
  saturn: z.string(),
  numerology: numerologySchema,
}).strict();

const legacyForecastMonthSchema = z.object({
  month: z.string().regex(/^\d{4}-\d{2}$/),
  personal_month: z.number().int(),
  theme: z.string(),
  opportunity: z.string(),
  challenge: z.string(),
  love_focus: z.string(),
  career_money_focus: z.string(),
  practical_step: z.string(),
  reflection_prompt: z.string(),
  affirmation: z.string(),
}).strict();

/** Historical phase-one content. This remains display/export compatible, never a generation target. */
export const legacyPaidReadingSchema = z.object({
  title: z.string(),
  prepared_for: z.string(),
  prepared_at: z.string().datetime(),
  celestial_signature: celestialSignatureSchema,
  love_and_relationships: legacyNarrativeSectionSchema,
  career_and_money: legacyNarrativeSectionSchema,
  purpose_and_growth: z.object({
    overview: z.string(),
    pattern_to_release: z.string(),
    quality_to_develop: z.string(),
  }).strict(),
  six_month_forecast: z.array(legacyForecastMonthSchema).length(6),
  closing_message: z.string(),
}).strict();

const paidForecastMonthSchema = z.object({
  month: z.string().regex(/^\d{4}-\d{2}$/),
  personal_month: z.number().int(),
  theme: z.string(),
  why_this_month_feels_different: z.string(),
  opportunity: z.string(),
  challenge: z.string(),
  love_and_relationships: z.string(),
  career_and_money: z.string(),
  practical_step: z.string(),
  reflection: z.string(),
  affirmation: z.string(),
}).strict();

export const PAID_READING_HORIZONS = [3, 6, 12] as const;

/** Current strict Structured Outputs target. */
export const paidReadingSchema = z.object({
  schema_version: z.literal('paid-v3'),
  title: z.string(),
  prepared_for: z.string(),
  prepared_at: z.string().datetime(),
  personalized_subtitle: z.string(),
  interpretive_quote: z.string(),
  reading_at_a_glance: z.object({
    love: z.string(),
    career: z.string(),
    growth: z.string(),
    major_turning_point: z.string(),
  }).strict(),
  celestial_signature: celestialSignatureSchema,
  core_pattern: z.object({
    overview: z.string(),
    strength: z.string(),
    growth_edge: z.string(),
  }).strict(),
  love_and_relationships: z.object({ overview: z.string() }).strict(),
  career_and_money: z.object({ overview: z.string() }).strict(),
  purpose_and_growth: z.object({
    overview: z.string(),
    pattern_to_release: z.string(),
    quality_to_develop: z.string(),
    where_growth_may_come_from: z.string(),
  }).strict(),
  six_month_forecast: z.array(paidForecastMonthSchema).refine((months) => PAID_READING_HORIZONS.includes(months.length as 3 | 6 | 12), { message: 'Paid forecast horizon must be 3, 6, or 12 months.' }),
  major_turning_point: z.object({
    month: z.string().regex(/^\d{4}-\d{2}$/),
    title: z.string(),
    why_this_period_stands_out: z.string(),
    what_may_be_shifting: z.string(),
    what_to_pay_attention_to: z.string(),
    how_to_work_with_this_energy: z.string(),
  }).strict(),
  six_month_pattern: z.object({
    months_1_2: z.string(),
    months_3_4: z.string(),
    months_5_6: z.string(),
    overview: z.string(),
  }).strict(),
  path_ahead: z.object({
    title: z.string(),
    message: z.string(),
    closing_quote: z.string(),
  }).strict(),
}).strict();

export const displayPaidReadingSchema = z.union([paidReadingSchema, legacyPaidReadingSchema]);

export const displayFreeReadingSchema = z.union([freeReadingSchema, historicalFreeV3ReadingSchema, historicalFreeV2ReadingSchema, legacyFreeReadingSchema]);

export type CurrentFreeReading = z.infer<typeof freeReadingSchema>;
export type HistoricalFreeV3Reading = z.infer<typeof historicalFreeV3ReadingSchema>;
export type HistoricalFreeV2Reading = z.infer<typeof historicalFreeV2ReadingSchema>;
export type LegacyFreeReading = z.infer<typeof legacyFreeReadingSchema>;
export type FreeReadingContent = z.infer<typeof displayFreeReadingSchema>;

export type CurrentPaidReading = z.infer<typeof paidReadingSchema>;
export type LegacyPaidReading = z.infer<typeof legacyPaidReadingSchema>;
export type PaidReading = z.infer<typeof displayPaidReadingSchema>;
export type ForecastMonth = PaidReading['six_month_forecast'][number];

export function isCurrentFreeReading(reading: FreeReadingContent): reading is CurrentFreeReading {
  return 'schema_version' in reading && reading.schema_version === FREE_READING_SCHEMA_VERSION;
}

export function isHistoricalFreeV3Reading(reading: FreeReadingContent): reading is HistoricalFreeV3Reading {
  return 'schema_version' in reading && reading.schema_version === 'free-v3';
}

export function isHistoricalFreeV2Reading(reading: FreeReadingContent): reading is HistoricalFreeV2Reading {
  return 'schema_version' in reading && reading.schema_version === 'free-v2';
}

export function isCurrentPaidReading(reading: PaidReading): reading is CurrentPaidReading {
  return 'schema_version' in reading && reading.schema_version === 'paid-v3';
}

export function paidReadingTurningPointIsInForecast(reading: CurrentPaidReading): boolean {
  return reading.six_month_forecast.some((month) => month.month === reading.major_turning_point.month);
}
